// ABOUTME: Central hook that connects ShDataGrid3 to userLayoutSlice (saved configs) and gridFilterSlice (runtime state).
// ABOUTME: Exposes action functions for the two explicit handoff moments — mount-time apply and explicit save.

import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { useGridFilter } from "../../../contexts/GridFilterContext";
import {
    setShGridColumns,
    updateFilter,
    updateSort,
    updateGroupBy,
    clearFilterForInstance,
    updatePageSize3,
    updateRowHeight,
} from "../../../newapi/global/filter/gridFilterSlice";
import {
    upsertConfig,
    deleteConfig as deleteConfigAction,
    renameConfig as renameConfigAction,
    setActiveConfig as setActiveConfigAction,
    setDefaultConfig as setDefaultConfigAction,
    applyConfigToFamily,
    unshareConfigForGrid,
    acknowledgeNewColumns as acknowledgeNewColumnsAction,
    setGeneralSetting,
    DEFAULT_ACTIVE_CONFIG_ID,
} from "../../../newapi/global/userLayout/userLayoutSlice";
import { SH_DATAGRID_DEFAULT_ROW_HEIGHT } from "../../../newapi/global/filter/DataGridGeneralOptions";

/**
 * useGridLayout(gridKey, family, columns)
 *
 * The "save file ↔ runtime" bridge for ShDataGrid3.
 *
 * - Reads saved-config metadata from userLayoutSlice (the save file).
 * - Exposes action functions that dispatch to BOTH slices for the two handoff moments:
 *     1. applySavedConfig() — reads userLayoutSlice, writes to gridFilterSlice. Called on mount.
 *     2. saveCurrent() / saveAs() — reads gridFilterSlice runtime, writes to userLayoutSlice. Called on explicit Save.
 * - Does NOT return runtime column state. The renderer reads that from gridFilterSlice directly.
 *
 * Family semantics:
 *   `family` is an optional declaration of sibling gridKeys the developer supplies at the call site.
 *   Accepts either an array of gridKeys OR an object whose values are gridKeys
 *   (e.g. `family={RELATION_ORDERS_FAMILY}` where the object maps named aliases → gridKeys).
 *   When "save for all" is ticked on save, the owner gridKey stores the config body plus a
 *   `sharedWith` list; each sibling gridKey receives only a pointer entry
 *   (`activeConfigId` set, `configs` empty). Mount-flow resolves pointers by finding the
 *   owner via the owner's `sharedWith` array. No config duplication, no mount-time scan.
 */
const useGridLayout = (gridKey, family, columns) => {
    const dispatch = useDispatch();
    const { index, id } = useGridFilter();

    // Normalize `family` to an array of gridKey strings regardless of input shape:
    //   - array of "gridKey" strings
    //   - array of { gridKey, label } objects
    //   - object keyed by alias → "gridKey" string
    //   - object keyed by alias → { gridKey, label }
    const familyGridKeys = useMemo(() => {
        const raw = Array.isArray(family)
            ? family
            : family && typeof family === "object"
                ? Object.values(family)
                : [];
        return raw
            .map((entry) =>
                typeof entry === "string"
                    ? entry
                    : entry && typeof entry === "object" && entry.gridKey
                        ? entry.gridKey
                        : null
            )
            .filter(Boolean);
    }, [family]);

    // --- Saved-config metadata (read from userLayoutSlice) ---------------------------------

    const gridEntry = useSelector((state) => {
        return state.userLayout?.document?.columnConfigs?.[gridKey] || null;
    });

    // Sibling entries, read for pointer-entry resolution. A pointer entry is a
    // gridEntry with `activeConfigId` set but no matching config under its own
    // `configs`. The owner is found by scanning `familyGridKeys` for a config
    // whose `sharedWith` list includes this gridKey. Bounded by the family size,
    // never a full-document scan.
    const siblingEntries = useSelector((state) => {
        const all = state.userLayout?.document?.columnConfigs || {};
        const out = {};
        for (const siblingKey of familyGridKeys) {
            if (siblingKey === gridKey) continue;
            if (all[siblingKey]) out[siblingKey] = all[siblingKey];
        }
        return out;
    });

    const resolvePointer = useCallback(
        (configId) => {
            if (!configId || configId === DEFAULT_ACTIVE_CONFIG_ID) return null;
            for (const sibling of Object.values(siblingEntries)) {
                const cfg = sibling?.configs?.[configId];
                if (cfg && Array.isArray(cfg.sharedWith) && cfg.sharedWith.includes(gridKey)) {
                    return cfg;
                }
            }
            return null;
        },
        [siblingEntries, gridKey]
    );

    // --- General settings (cross-grid user preferences) ------------------------------------
    // Persisted in userLayoutSlice.document.generalSettings. Mount-flow seeds the runtime
    // mirrors in gridFilterSlice.settings from here, and writes go to both slices so the
    // runtime reflects the change immediately.
    const generalSettings = useSelector((state) => {
        return state.userLayout?.document?.generalSettings || null;
    });

    const activeConfigId = gridEntry?.activeConfigId || DEFAULT_ACTIVE_CONFIG_ID;
    const defaultConfigId = gridEntry?.defaultConfigId || DEFAULT_ACTIVE_CONFIG_ID;

    const activeConfig = useMemo(() => {
        if (!gridEntry || activeConfigId === DEFAULT_ACTIVE_CONFIG_ID) return null;
        // Own config wins; if absent, this is a pointer entry — resolve via a sibling owner.
        return gridEntry.configs?.[activeConfigId] || resolvePointer(activeConfigId) || null;
    }, [gridEntry, activeConfigId, resolvePointer]);

    // The config that auto-loads on mount (set via "set as active").
    const defaultConfig = useMemo(() => {
        if (!gridEntry || defaultConfigId === DEFAULT_ACTIVE_CONFIG_ID) return null;
        return gridEntry.configs?.[defaultConfigId] || resolvePointer(defaultConfigId) || null;
    }, [gridEntry, defaultConfigId, resolvePointer]);

    const configs = useMemo(() => {
        // Tag each config with `isShared` so consumers (dropdown, save dialog) can
        // render the "shared from another view" indicator and detach action without
        // having to repeat the ownership check themselves.
        const own = gridEntry?.configs
            ? Object.values(gridEntry.configs).map((c) => ({ ...c, isShared: false }))
            : [];
        const ownIds = new Set(own.map((c) => c.id));
        const shared = [];
        for (const sibling of Object.values(siblingEntries)) {
            if (!sibling?.configs) continue;
            for (const cfg of Object.values(sibling.configs)) {
                if (ownIds.has(cfg.id)) continue;
                if (Array.isArray(cfg.sharedWith) && cfg.sharedWith.includes(gridKey)) {
                    shared.push({ ...cfg, isShared: true });
                }
            }
        }
        return [...own, ...shared];
    }, [gridEntry, siblingEntries, gridKey]);

    // --- New-column indicator ---------------------------------------------------------------
    // Columns that are visible-by-default in code but absent from the active saved config,
    // and not already acknowledged by the user via the + popover.

    const { newColumnFields, newColumnCount } = useMemo(() => {
        if (!activeConfig || !Array.isArray(columns)) {
            return { newColumnFields: [], newColumnCount: 0 };
        }
        const savedFieldSet = new Set((activeConfig.columns || []).map((c) => c.field));
        const acknowledgedSet = new Set(activeConfig.acknowledgedColumns || []);
        const newFields = columns
            .filter((c) => !c.hide)
            .filter((c) => !savedFieldSet.has(c.field))
            .filter((c) => !acknowledgedSet.has(c.field))
            .map((c) => c.field);
        return { newColumnFields: newFields, newColumnCount: newFields.length };
    }, [activeConfig, columns]);

    // --- Runtime snapshot (read from gridFilterSlice, used by saveCurrent/saveAs) -----------
    // This is a read-only peek, NOT a subscription the renderer uses for drawing.

    const runtimeInstance = useSelector((state) => {
        return state.gridFilter?.viewInstances?.[index]?.[id] || null;
    });

    // --- Helpers ----------------------------------------------------------------------------

    /**
     * Build the minimal runtime-columns payload from either a saved config or the raw
     * code-defined columns prop. Runtime shape for gridFilterSlice is
     *   [{ column, visible, pinned }]
     * where `column` is the field name. We deliberately do NOT store renderCell / width /
     * headerName in runtime — those come from the code columns prop at render time.
     */
    const resolveRuntimeColumns = useCallback(
        (config, { isLocallyOwned = false } = {}) => {
            if (!Array.isArray(columns)) return [];

            if (!config || !Array.isArray(config.columns)) {
                return columns.map((col) => ({
                    column: col.field,
                    visible: col.hide !== true,
                    pinned: null,
                }));
            }

            const codeFieldSet = new Set(columns.map((c) => c.field));
            const resolved = config.columns
                .filter((sc) => codeFieldSet.has(sc.field))
                .map((sc) => ({
                    column: sc.field,
                    visible: sc.visible !== false,
                    pinned: sc.pinned ?? null,
                }));

            // For locally-owned saved configs the user's saved column list is
            // authoritative — new code columns must NOT auto-append at the end.
            // They surface via the + popover through `newColumnFields` instead.
            // Linked/shared configs still auto-append because the owning sibling
            // may not know about this grid's code columns (schema drift).
            if (isLocallyOwned) {
                return resolved;
            }

            const knownFields = new Set(resolved.map((c) => c.column));
            const appended = columns
                .filter((c) => !knownFields.has(c.field))
                .map((c) => ({
                    column: c.field,
                    visible: c.hide !== true,
                    pinned: null,
                }));

            return [...resolved, ...appended];
        },
        [columns]
    );

    /**
     * Returns the config's groupBy if the field it groups on exists in this grid's
     * code-declared columns, otherwise null. Shared configs may group on a column
     * the sibling doesn't have — applying that verbatim would group by a phantom
     * field. Dropping silently leaves the sibling ungrouped, which is correct.
     */
    const resolveGroupByForColumns = useCallback(
        (groupBy) => {
            if (!groupBy || !groupBy.field) return null;
            const hasField = Array.isArray(columns) && columns.some((c) => c.field === groupBy.field);
            return hasField ? groupBy : null;
        },
        [columns]
    );

    /**
     * Build a save-file config payload from the current runtime state.
     * Used by both saveCurrent (overwrite) and saveAs (create new).
     */
    const currentRowHeight = useSelector(
        (state) => state.gridFilter?.settings?.rowHeight ?? SH_DATAGRID_DEFAULT_ROW_HEIGHT
    );

    const buildConfigFromRuntime = useCallback(
        ({ id: configId, name, includeFilters, includeSort }) => {
            const runtimeCols = runtimeInstance?.columns || [];
            return {
                id: configId,
                name: name || "Untitled",
                columns: runtimeCols.map((rc) => ({
                    field: rc.column,
                    visible: rc.visible !== false,
                    pinned: rc.pinned ?? null,
                })),
                filters: includeFilters ? runtimeInstance?.filter || null : null,
                sortModel: includeSort ? runtimeInstance?.sortModel || null : null,
                groupBy: runtimeInstance?.groupBy ?? null,
                rowHeight: currentRowHeight,
            };
        },
        [runtimeInstance, currentRowHeight]
    );

    // --- Actions ----------------------------------------------------------------------------

    /**
     * Mount-flow: read the active saved config from userLayoutSlice and dispatch it into
     * gridFilterSlice so the grid renders with the saved state. If there is no active
     * saved config, dispatches the code-derived defaults (columns from the prop).
     */
    const applySavedConfig = useCallback(() => {
        // On mount, load ONLY the DEFAULT config (the user's intentional go-to view).
        // If no default is set, reset activeConfigId to "default" and render code
        // defaults — a saved-but-not-defaulted config is reachable via the dropdown
        // but must never auto-apply on reload.
        const mountConfig = defaultConfig;
        if (defaultConfigId !== DEFAULT_ACTIVE_CONFIG_ID) {
            dispatch(setActiveConfigAction({ gridKey, configId: defaultConfigId }));
        } else if (activeConfigId !== DEFAULT_ACTIVE_CONFIG_ID) {
            dispatch(setActiveConfigAction({ gridKey, configId: DEFAULT_ACTIVE_CONFIG_ID }));
        }

        // Shared configs resolved via pointer (owner !== this gridKey) apply
        // columns + grouping only — filters and sortModel are tab-specific and
        // must not leak across siblings (e.g. .active saved with state=active
        // would otherwise break .backordered's data query).
        const isOwnedLocally = !!(mountConfig && gridEntry?.configs?.[mountConfig.id]);

        const runtimeColumns = resolveRuntimeColumns(mountConfig, { isLocallyOwned: isOwnedLocally });
        dispatch(setShGridColumns({ index, id, columns: runtimeColumns }));

        if (isOwnedLocally && mountConfig?.sortModel && typeof mountConfig.sortModel === "object") {
            dispatch(updateSort({ index, id, sortModel: mountConfig.sortModel }));
        }

        if (isOwnedLocally && mountConfig?.filters && typeof mountConfig.filters === "object") {
            dispatch(clearFilterForInstance({ index, id }));
            Object.entries(mountConfig.filters).forEach(([filterKey, value]) => {
                dispatch(updateFilter({ index, id, filterKey, value }));
            });
        }

        dispatch(updateGroupBy({ index, id, groupBy: resolveGroupByForColumns(mountConfig?.groupBy) }));

        // Per-config row height — fall back to default if the config has none.
        const configRowHeight = mountConfig?.rowHeight ?? SH_DATAGRID_DEFAULT_ROW_HEIGHT;
        dispatch(updateRowHeight(configRowHeight));

        if (generalSettings) {
            if (typeof generalSettings.pageSize3 === "number") {
                dispatch(updatePageSize3(generalSettings.pageSize3));
            }
        }
    }, [dispatch, gridKey, index, id, gridEntry, defaultConfig, defaultConfigId, activeConfigId, resolveRuntimeColumns, resolveGroupByForColumns, generalSettings]);

    /**
     * Save-flow (overwrite existing active config).
     * No-op if there is no active saved config — caller should use saveAs instead.
     * Options:
     *   { name?, includeFilters?, includeSort?, setAsActive?,
     *     shareWith?: string[], applyToAllInFamily?: boolean }
     * `shareWith` (from the MSaveGridConfig dropdown) takes precedence when provided.
     * `applyToAllInFamily` is a back-compat fallback that shares with every family member.
     */
    const saveCurrent = useCallback(
        (options = {}) => {
            if (!activeConfig) return null;

            const siblingGridKeys = Array.isArray(options.shareWith)
                ? options.shareWith.filter((key) => key !== gridKey)
                : options.applyToAllInFamily
                    ? familyGridKeys.filter((key) => key !== gridKey)
                    : [];

            const config = buildConfigFromRuntime({
                id: activeConfig.id,
                name: options.name || activeConfig.name,
                includeFilters: !!options.includeFilters,
                includeSort: !!options.includeSort,
            });

            // When sharing, record the sibling list on the owner's config so mount-flow
            // can resolve pointer entries back to this owner later.
            if (siblingGridKeys.length > 0) {
                config.sharedWith = siblingGridKeys;
            }

            dispatch(upsertConfig({ gridKey, config }));

            if (options.setAsActive) {
                dispatch(setDefaultConfigAction({ gridKey, configId: activeConfig.id }));
            }

            if (siblingGridKeys.length > 0) {
                dispatch(applyConfigToFamily({ configId: activeConfig.id, siblingGridKeys }));

                // When "set as default" is ticked alongside linked tabs, propagate
                // the default to each linked sibling so their mount-flow also
                // auto-loads this shared view.
                if (options.setAsActive) {
                    for (const siblingKey of siblingGridKeys) {
                        dispatch(setDefaultConfigAction({ gridKey: siblingKey, configId: activeConfig.id }));
                    }
                }
            }

            return config;
        },
        [dispatch, gridKey, familyGridKeys, activeConfig, buildConfigFromRuntime]
    );

    /**
     * Save-flow (create new config).
     * Options:
     *   { name, includeFilters?, includeSort?, setAsActive?,
     *     shareWith?: string[], applyToAllInFamily?: boolean }
     */
    const saveAs = useCallback(
        (options = {}) => {
            const configId = `cfg_${uuidv4()}`;

            const siblingGridKeys = Array.isArray(options.shareWith)
                ? options.shareWith.filter((key) => key !== gridKey)
                : options.applyToAllInFamily
                    ? familyGridKeys.filter((key) => key !== gridKey)
                    : [];

            const config = buildConfigFromRuntime({
                id: configId,
                name: options.name || "Untitled",
                includeFilters: !!options.includeFilters,
                includeSort: !!options.includeSort,
            });

            if (siblingGridKeys.length > 0) {
                config.sharedWith = siblingGridKeys;
            }

            dispatch(upsertConfig({ gridKey, config }));

            // Also view it immediately.
            dispatch(setActiveConfigAction({ gridKey, configId }));

            // "Set as active" = make this config the default that auto-loads on
            // mount (persists to defaultConfigId, separate from activeConfigId).
            if (options.setAsActive) {
                dispatch(setDefaultConfigAction({ gridKey, configId }));
            }

            if (siblingGridKeys.length > 0) {
                dispatch(applyConfigToFamily({ configId, siblingGridKeys }));

                // Propagate default to linked siblings so their mount-flow also
                // auto-loads this shared view.
                if (options.setAsActive) {
                    for (const siblingKey of siblingGridKeys) {
                        dispatch(setDefaultConfigAction({ gridKey: siblingKey, configId }));
                    }
                }
            }

            return config;
        },
        [dispatch, gridKey, familyGridKeys, activeConfigId, buildConfigFromRuntime]
    );

    /**
     * Switch active config and immediately apply it to runtime.
     * Pass configId === DEFAULT_ACTIVE_CONFIG_ID ("default") to reset to code defaults.
     */
    const setActiveConfig = useCallback(
        (configId) => {
            dispatch(setActiveConfigAction({ gridKey, configId }));

            // Apply happens in the next tick via the applySavedConfig consumer — but we
            // can also dispatch the new config's runtime state directly here so there's
            // no race between the slice update and the component re-reading activeConfig.
            // Own config wins; if absent, fall back to a shared config owned by a
            // family sibling (pointer resolution). Without this, clicking a shared
            // config in the dropdown on a sibling did nothing because the lookup
            // only checked the local gridEntry.
            const nextConfig =
                configId === DEFAULT_ACTIVE_CONFIG_ID
                    ? null
                    : gridEntry?.configs?.[configId] || resolvePointer(configId) || null;

            // See applySavedConfig: shared configs owned by a sibling apply columns
            // + grouping only. Tab-specific data state (filters, sort) stays local.
            const isOwnedLocally = !!(nextConfig && gridEntry?.configs?.[nextConfig.id]);

            const runtimeColumns = resolveRuntimeColumns(nextConfig, { isLocallyOwned: isOwnedLocally });
            dispatch(setShGridColumns({ index, id, columns: runtimeColumns }));

            if (isOwnedLocally && nextConfig?.sortModel) {
                dispatch(updateSort({ index, id, sortModel: nextConfig.sortModel }));
            } else if (!nextConfig) {
                dispatch(updateSort({ index, id, sortModel: {} }));
            }

            if (isOwnedLocally) {
                dispatch(clearFilterForInstance({ index, id }));
                if (nextConfig?.filters) {
                    Object.entries(nextConfig.filters).forEach(([filterKey, value]) => {
                        dispatch(updateFilter({ index, id, filterKey, value }));
                    });
                }
            } else if (!nextConfig) {
                // Reset-to-default: clear filters.
                dispatch(clearFilterForInstance({ index, id }));
            }

            dispatch(updateGroupBy({ index, id, groupBy: resolveGroupByForColumns(nextConfig?.groupBy) }));

            // Apply per-config row height if stored, otherwise reset to default.
            const nextRowHeight = nextConfig?.rowHeight ?? SH_DATAGRID_DEFAULT_ROW_HEIGHT;
            dispatch(updateRowHeight(nextRowHeight));
        },
        [dispatch, gridKey, index, id, gridEntry, resolvePointer, resolveRuntimeColumns, resolveGroupByForColumns]
    );

    /**
     * Runtime-flow: update the active grouping. Dispatches to gridFilterSlice only.
     * Does NOT touch userLayoutSlice — the change is a runtime edit that only
     * persists if the user explicitly hits Save afterwards.
     *
     * Payload shape: null (disable grouping) or { field, granularity }.
     */
    const setGroupBy = useCallback(
        (groupBy) => {
            dispatch(updateGroupBy({ index, id, groupBy: groupBy ?? null }));
        },
        [dispatch, index, id]
    );

    const renameConfig = useCallback(
        (configId, name) => {
            dispatch(renameConfigAction({ gridKey, configId, name }));
        },
        [dispatch, gridKey]
    );

    const deleteConfig = useCallback(
        (configId) => {
            const wasActive = activeConfigId === configId;
            dispatch(deleteConfigAction({ gridKey, configId }));
            // If we just deleted the active config, reset runtime to code defaults.
            if (wasActive) {
                const runtimeColumns = resolveRuntimeColumns(null);
                dispatch(setShGridColumns({ index, id, columns: runtimeColumns }));
            }
        },
        [dispatch, gridKey, index, id, activeConfigId, resolveRuntimeColumns]
    );

    /**
     * Detach a shared config from THIS gridKey only ("unmount" action).
     * The owner's copy is untouched; other siblings still have it. If the detached
     * config was active, runtime falls back to code defaults.
     */
    const detachSharedConfig = useCallback(
        (configId, options = {}) => {
            const wasActive = activeConfigId === configId;
            dispatch(unshareConfigForGrid({ gridKey, configId }));
            // Full runtime reset via the existing default-view path. Clears
            // columns/sort/filters/groupBy/rowHeight in one shot — no drift.
            // Callers that are setting a NEW active config before detaching (e.g.
            // Save-As on a shared view) can pass `skipRuntimeReset: true` to keep
            // the just-activated config in place.
            if (wasActive && !options.skipRuntimeReset) {
                setActiveConfig(DEFAULT_ACTIVE_CONFIG_ID);
            }
        },
        [dispatch, gridKey, activeConfigId, setActiveConfig]
    );

    const resetToDefault = useCallback(() => {
        setActiveConfig(DEFAULT_ACTIVE_CONFIG_ID);
    }, [setActiveConfig]);

    /**
     * Clear the new-column indicator by marking every current code field as acknowledged
     * on the active config. Called when the user opens the + popover.
     */
    const acknowledgeNewColumns = useCallback(() => {
        if (!activeConfig || !Array.isArray(columns)) return;
        const fields = columns.map((c) => c.field);
        dispatch(acknowledgeNewColumnsAction({ gridKey, fields }));
    }, [dispatch, gridKey, activeConfig, columns]);

    /**
     * Update the runtime row height. Row height is per-config — it gets
     * persisted into the config payload when the user explicitly saves.
     */
    const setRowHeight = useCallback(
        (value) => {
            dispatch(updateRowHeight(value));
        },
        [dispatch]
    );

    const setDefaultConfig = useCallback(
        (configId) => {
            dispatch(setDefaultConfigAction({ gridKey, configId }));
        },
        [dispatch, gridKey]
    );

    const setPageSize = useCallback(
        (value) => {
            dispatch(setGeneralSetting({ key: "pageSize3", value }));
            dispatch(updatePageSize3(value));
        },
        [dispatch]
    );

    // True when the currently active config is owned by THIS gridKey (in its own
    // `configs` map). False when it's a pointer to a sibling-owned (shared) config
    // or when no config is active. Consumers use this to gate owner-only behaviour —
    // e.g. syncing the local `config` prop filters back into gridFilterSlice on
    // siblings even while a shared view is active.
    const isActiveConfigLocal = !!(
        activeConfig && gridEntry?.configs?.[activeConfig.id]
    );

    return {
        activeConfig,
        activeConfigId,
        defaultConfigId,
        isActiveConfigLocal,
        configs,
        newColumnFields,
        newColumnCount,
        generalSettings,
        actions: {
            applySavedConfig,
            saveCurrent,
            saveAs,
            setActiveConfig,
            renameConfig,
            deleteConfig,
            detachSharedConfig,
            resetToDefault,
            acknowledgeNewColumns,
            setRowHeight,
            setPageSize,
            setDefaultConfig,
            setGroupBy,
        },
    };
};

export default useGridLayout;
export { DEFAULT_ACTIVE_CONFIG_ID };
